import { useEffect, useState } from 'react';
import { api } from '../api.js';

/**
 * The Ledger — her whole ranked record, publicly.
 *
 * Identity (rank, mastery) comes from /api/accounts; the depth — rates,
 * records, champion pools, honors, highlights — comes from /api/accounts/detail,
 * computed from every stored ranked game. Nothing here reveals whether she is
 * playing right now except the live marker, which only exists on air.
 */

interface AccountCard {
  key: string;
  riotId: string | null;
  level: number | null;
  tier: string | null;
  division: string | null;
  leaguePoints: number | null;
  rankedWins: number | null;
  rankedLosses: number | null;
  aniviaPoints: number | null;
  games: number;
  winRate: number | null;
  recentForm: boolean[];
  unrankedSample: boolean;
  bookable: boolean;
  live: boolean;
}

interface ChampionLine {
  champion: string;
  games: number;
  wins: number;
  kda: number;
  avgKills: number;
  csPerMin: number;
}

interface RecordLine {
  value: string;
  champion: string;
  line: string;
  win: boolean;
  at: number | null;
}

interface AccountDetail {
  key: string;
  games: number;
  wins: number;
  hours: number;
  kda: number;
  avg: { kills: number; deaths: number; assists: number };
  killParticipation: number | null;
  csPerMin: number;
  goldPerMin: number;
  dmgPerMin: number;
  visionPerMin: number;
  pentas: number;
  winStreak: number;
  lossStreak: number;
  championCount: number;
  champions: ChampionLine[];
  records: {
    mostKills: RecordLine | null;
    bestKda: RecordLine | null;
    biggestMulti: RecordLine | null;
  };
}

interface Honor {
  title: string;
  note: string;
  account: string;
  value: string;
}

export function LedgerPage() {
  const [accounts, setAccounts] = useState<AccountCard[] | null>(null);
  const [detail, setDetail] = useState<{
    accounts: AccountDetail[];
    honors: Honor[];
    highlights: string[];
    founders?: Array<{ name: string; at: number }>;
  } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [cards, deep] = await Promise.all([
          api.accounts<AccountCard>(),
          api.accountsDetail(),
        ]);
        if (!cancelled) {
          setAccounts(cards.accounts);
          setDetail(deep);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    void load();
    const timer = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (failed) return <div className="page"><p className="note">Could not load the ledger.</p></div>;
  if (!accounts) return <div className="page"><p className="note">Loading…</p></div>;

  const detailFor = (key: string) => detail?.accounts.find((d) => d.key === key) ?? null;
  const hasDepth = (detail?.accounts.length ?? 0) > 0;

  return (
    <div className="page">
      <header className="page-head">
        <h1>The Ledger</h1>
        <p>
          Three accounts, one player. Every number below comes from her ranked history and is
          what the book prices against.
        </p>
      </header>

      {detail && detail.highlights.length > 0 && <HighlightStrip items={detail.highlights} />}

      <div className="accounts">
        {accounts.map((account) => (
          <AccountBlock key={account.key} account={account} detail={detailFor(account.key)} />
        ))}
      </div>

      {!hasDepth && (
        <p className="note">
          Full match history is still syncing — rates, records, and honors appear once it lands.
        </p>
      )}

      {detail && detail.honors.length > 0 && (
        <>
          <h2 className="section">Honors — her, versus herself</h2>
          <div className="honors">
            {detail.honors.map((honor) => (
              <div key={honor.title} className="honor">
                <span className="honor-title">{honor.title}</span>
                <span className="honor-holder">{holderName(accounts, honor.account)}</span>
                <span className="honor-value num">{honor.value}</span>
                <span className="honor-note">{honor.note}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {detail && (detail.founders?.length ?? 0) > 0 && (
        <>
          <h2 className="section">Founders&rsquo; Row</h2>
          <ul className="founders">
            {detail.founders!.map((founder) => (
              <li key={`${founder.name}-${founder.at}`} className="founder">
                <span className="founder-name">{founder.name}</span>
                <span className="founder-rule" />
                <span className="founder-date">
                  {new Date(founder.at).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {hasDepth && detail && detail.accounts.length > 1 && (
        <>
          <h2 className="section">Head to head</h2>
          <div className="versus-scroll">
            <table className="versus">
              <thead>
                <tr>
                  <th className="label">Account</th>
                  <th className="label">Games</th>
                  <th className="label">Win</th>
                  <th className="label">KDA</th>
                  <th className="label">KP</th>
                  <th className="label">CS/m</th>
                  <th className="label">Dmg/m</th>
                  <th className="label">Vis/m</th>
                  <th className="label">Pentas</th>
                </tr>
              </thead>
              <tbody>
                {detail.accounts.map((d) => (
                  <tr key={d.key}>
                    <td className="versus-name">{holderName(accounts, d.key)}</td>
                    <td className="num">{d.games}</td>
                    <td className="num">{d.games > 0 ? `${Math.round((d.wins / d.games) * 100)}%` : '—'}</td>
                    <td className="num">{d.kda}</td>
                    <td className="num">{d.killParticipation !== null ? `${Math.round(d.killParticipation * 100)}%` : '—'}</td>
                    <td className="num">{d.csPerMin}</td>
                    <td className="num">{d.dmgPerMin}</td>
                    <td className="num">{d.visionPerMin}</td>
                    <td className="num">{d.pentas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function holderName(accounts: AccountCard[], key: string): string {
  return accounts.find((a) => a.key === key)?.riotId ?? key;
}

/** One highlight at a time, rotating — the games worth talking about. */
function HighlightStrip({ items }: { items: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % items.length), 5_000);
    return () => clearInterval(timer);
  }, [items.length]);

  return (
    <div className="highlight-strip">
      <span className="highlight-tag">Record book</span>
      <span key={index} className="highlight-text">
        {items[index]}
      </span>
    </div>
  );
}

function AccountBlock({ account, detail }: { account: AccountCard; detail: AccountDetail | null }) {
  return (
    <article className={`account ${account.live ? 'is-live' : ''}`}>
      <div className="account-id">
        <div className="account-name">
          {account.live && <span className="live-dot" title="Playing right now" />}
          {account.riotId ?? account.key}
        </div>
        <div className="account-rank">
          {account.tier ? (
            <>
              {account.tier} {account.division}
              {account.leaguePoints !== null && <span className="lp num"> {account.leaguePoints} LP</span>}
            </>
          ) : (
            <span className="unranked">Unranked</span>
          )}
          {account.level !== null && <span className="level"> · level {account.level}</span>}
        </div>
        {account.aniviaPoints !== null && (
          <div className="account-mastery">
            {Math.round(account.aniviaPoints / 1000)}k Anivia mastery
          </div>
        )}
        {detail && (
          <div className="account-hours">
            {detail.hours}h on the rift · {detail.championCount} champions
          </div>
        )}
      </div>

      <div className="account-body">
        {detail ? (
          <dl className="account-stats">
            <Stat label="Win rate" value={detail.games > 0 ? `${((detail.wins / detail.games) * 100).toFixed(1)}%` : '—'} sub={`${detail.games} ranked`} />
            <Stat label="KDA" value={String(detail.kda)} sub={`${detail.avg.kills}/${detail.avg.deaths}/${detail.avg.assists}`} />
            <Stat label="Kill part." value={detail.killParticipation !== null ? `${Math.round(detail.killParticipation * 100)}%` : '—'} sub="of team kills" />
            <Stat label="CS / min" value={String(detail.csPerMin)} sub="farm" />
            <Stat label="Dmg / min" value={String(detail.dmgPerMin)} sub="to champions" />
            <Stat label="Gold / min" value={String(detail.goldPerMin)} sub="income" />
            <Stat label="Vision / min" value={String(detail.visionPerMin)} sub="score" />
            <Stat label="Streaks" value={`W${detail.winStreak}`} sub={`worst L${detail.lossStreak}`} />
          </dl>
        ) : (
          <dl className="account-stats">
            <Stat label="Win rate" value={account.winRate !== null ? `${(account.winRate * 100).toFixed(1)}%` : '—'} sub={account.games > 0 ? `${account.games} games` : 'no history'} />
          </dl>
        )}

        <div className="account-form">
          {account.recentForm.length > 0 ? (
            account.recentForm.map((won, i) => (
              <span key={i} className={won ? 'pip w' : 'pip l'}>
                {won ? 'W' : 'L'}
              </span>
            ))
          ) : (
            <span className="form-empty">no recent ranked games</span>
          )}
        </div>

        {detail && detail.records.mostKills && (
          <div className="account-records">
            <RecordChip label="Most kills" record={detail.records.mostKills} />
            <RecordChip label="Best game" record={detail.records.bestKda} />
            <RecordChip label="Multikill" record={detail.records.biggestMulti} />
          </div>
        )}

        {detail && detail.champions.length > 0 && (
          <table className="champ-table">
            <thead>
              <tr>
                <th className="label">Champion</th>
                <th className="label">Games</th>
                <th className="label">Win</th>
                <th className="label">KDA</th>
                <th className="label">Kills</th>
                <th className="label">CS/m</th>
              </tr>
            </thead>
            <tbody>
              {detail.champions.map((c) => (
                <tr key={c.champion}>
                  <td className="champ-name">{c.champion}</td>
                  <td className="num">{c.games}</td>
                  <td className="num">{Math.round((c.wins / c.games) * 100)}%</td>
                  <td className="num">{c.kda}</td>
                  <td className="num">{c.avgKills}</td>
                  <td className="num">{c.csPerMin}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!account.bookable && (
          <p className="account-note">
            {account.unrankedSample
              ? 'No ranked history yet — nothing on this account is bookable, and these numbers come from normal games.'
              : 'Not enough history to price a market honestly.'}
          </p>
        )}
      </div>
    </article>
  );
}

function RecordChip({ label, record }: { label: string; record: RecordLine | null }) {
  if (!record) return null;
  return (
    <div className="record-chip">
      <span className="label">{label}</span>
      <span className="record-chip-value">{record.value}</span>
      <span className="record-chip-sub">
        {record.line} on {record.champion}
      </span>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="stat">
      <dt className="label">{label}</dt>
      <dd>
        <span className="stat-value num">{value}</span>
        <span className="stat-sub">{sub}</span>
      </dd>
    </div>
  );
}
